import type { ContactsResponse, PlanGroup } from '@ctct/domain-layer';
import { envs } from '@ctct/infrastructure-layer';
import type { ContactsTierSelectPageStore } from '@ctct/state-client-layer';

import type { TEventSource } from '../../../helpers';
import type { TranslationGetter } from '../../../types';
import { contactsTierSelectEventBus } from '../ContactsTierSelectEventBus';
import type { ContactsTierSelectUrlAdapter, Flags } from '../types';

export class ContactsService {
  constructor(
    private pageStore: ContactsTierSelectPageStore,
    private planGroup: PlanGroup,
    private contactsCount: ContactsResponse,
    private urlAdapter: ContactsTierSelectUrlAdapter,
    private flags: Flags,
    private t: TranslationGetter
  ) {}

  public api() {
    return {
      setContactsTier: (contactsTier: string) => this.pageStore.setState({ contactsTier }),
      getContactTier: () => this.pageStore.getState().contactsTier,
      disable: () => this.pageStore.setState({ contactsTierSelectDisabled: true }),
      enable: () => this.pageStore.setState({ contactsTierSelectDisabled: false }),
    };
  }

  public data() {
    return {
      getTiersOptions: () =>
        this.planGroup.getPriceTiers().map((tier) => {
          const isUnlimited = this.planGroup.isUnlimitedTier(tier);
          const isMWebVariantEnabled = this.flags?.isMWebVariantEnabled;

          if (isUnlimited) {
            return {
              value: String(tier.tierMin),
              label: this.t('application-layer.pricing-configurator.contact-tiers-unlimited'),
            };
          }

          if (isMWebVariantEnabled) {
            return {
              value: String(tier.tierMin),
              label: `${tier.tierMax.toLocaleString()} Contacts`,
            };
          }

          return {
            value: String(tier.tierMin),
            label: `${tier.tierMin.toLocaleString()} - ${tier.tierMax.toLocaleString()}`,
          };
        }),

      getLabels: () => {
        const isMWebVariantEnabled = this.flags?.isMWebVariantEnabled;

        if (this.flags?.isSmsBundle) {
          return {
            contactTiersLabel: isMWebVariantEnabled
              ? ''
              : this.t('application-layer.pricing-configurator.contact-tiers-sms-bundle'),
          };
        }

        if (isMWebVariantEnabled) {
          return {
            contactTiersLabel: '',
          };
        }

        return {
          contactTiersLabel: this.t('application-layer.pricing-configurator.contact-tiers'),
        };
      },
    };
  }

  public handlers() {
    return {
      onContactsTierChange: (newTier: string) => {
        this.updateContactsTier(newTier, 'user');
      },
    };
  }

  public onMount() {
    const skipInitializationFromUrl = this.flags?.skipInitializationFromUrl;
    const { contactCount, deleteContactCount } = this.urlAdapter.getContactsTierSelectParams();
    const contactsFromUrl = parseInt(`${contactCount}`, 10);

    if (envs.isSecure()) deleteContactCount();

    if (
      typeof contactsFromUrl === 'number' &&
      !Number.isNaN(contactsFromUrl) &&
      !skipInitializationFromUrl &&
      !this.flags?.isCoreMutationLockedAccount
    ) {
      const tier = this.getTierByContactsCount(contactsFromUrl);

      this.updateContactsTier(tier, 'onMount');

      return;
    }

    if (this.pageStore.getState().contactsTier === '0') {
      const tier = this.getTierByContactsCount(this.getEffectiveContactsCountForTierInit());

      this.updateContactsTier(tier, 'onMount');
    }
  }

  private getEffectiveContactsCountForTierInit(): number {
    const actualUsage = this.contactsCount.contacts_count ?? 0;
    const purchasedTierMin = this.planGroup.getPurchasedContactTierMin();

    if (purchasedTierMin === null) {
      return actualUsage;
    }

    return Math.max(purchasedTierMin, actualUsage);
  }

  private updateContactsTier(newTier: string, source: TEventSource) {
    if (this.flags?.isCoreMutationLockedAccount && source !== 'onMount' && source !== 'asyncResponse') {
      return;
    }

    this.pageStore.setState({ contactsTier: newTier });
    contactsTierSelectEventBus.emit('contactTierChanged', newTier, { source });
  }

  private getTierByContactsCount(contactsCount: number) {
    const contactTiers = this.planGroup.getPriceTiers();
    const currentContactTier = this.pageStore.getState().contactsTier;
    const matchingTier = contactTiers.find(
      (tier) => tier.tierMin <= contactsCount && (tier.tierMax >= contactsCount || tier.tierMax === -1)
    );

    return String(matchingTier?.tierMin ?? currentContactTier);
  }
}
